import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useNotifications } from "@/store/notifications/NotificationsProvider";
import type { NotificationItem } from "@/store/notifications/types";
import { cn } from "@/utils/cn";

const TYPE_ICONS: Record<string, string> = {
  inscription: "ri-user-add-line",
  paiement_soumis: "ri-hand-coin-line",
  paiement_valide: "ri-checkbox-circle-line",
  paiement_refuse: "ri-close-circle-line",
  invitation_employe: "ri-user-follow-line",
  impaye: "ri-alert-line",
  essai: "ri-hourglass-line",
};

const TYPE_TONES: Record<string, string> = {
  inscription: "bg-accent-100 text-accent-700",
  paiement_soumis: "bg-primary-100 text-primary-700",
  paiement_valide: "bg-secondary-100 text-secondary-700",
  paiement_refuse: "bg-background-200 text-foreground-700",
  invitation_employe: "bg-primary-100 text-primary-700",
  impaye: "bg-accent-100 text-accent-700",
  essai: "bg-secondary-100 text-secondary-700",
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `il y a ${days} j`;
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

export default function NotificationsBell() {
  const { notifications, unreadCount, loading, error, realtime, refresh, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useClickOutside(ref, () => setOpen(false), open);

  const handleItemClick = (item: NotificationItem) => {
    void markRead(item.id);
    setOpen(false);
    if (item.lien) navigate(item.lien);
  };

  // La cloche n'affiche QUE les notifications non lues : dès qu'une notification
  // est marquée lue (clic), elle disparaît immédiatement de la liste, et ne
  // revient pas après actualisation (le serveur renvoie `lu = true`).
  const visibleNotifications = useMemo(
    () => notifications.filter((item) => !item.lu),
    [notifications]
  );

  const badge = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} non lues` : "Notifications"}
        aria-expanded={open}
        className="relative flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950 cursor-pointer"
      >
        <i className="ri-notification-3-line text-xl" aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-primary-600 px-1 text-[10px] font-label font-semibold leading-none text-background-50">
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-background-200 bg-background-50 animate-scale-in sm:w-96">
          <div className="flex items-center justify-between gap-2 border-b border-background-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <h2 className="font-heading text-sm font-semibold text-foreground-950">Notifications</h2>
              {unreadCount > 0 && (
                <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-label font-medium text-primary-800">
                  {badge}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
              >
                <i className="ri-check-double-line text-sm" aria-hidden="true" />
                Tout marquer comme lu
              </button>
            )}
          </div>

          <div className="max-h-[70vh] overflow-y-auto sm:max-h-96">
            {loading && visibleNotifications.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-foreground-500">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-background-300 border-t-primary-500" />
                Chargement…
              </div>
            ) : error && visibleNotifications.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                  <i className="ri-error-warning-line text-xl" aria-hidden="true" />
                </span>
                <p className="text-sm text-foreground-700">{error}</p>
                <button
                  type="button"
                  onClick={() => void refresh()}
                  className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-xs font-label font-medium text-foreground-800 transition-colors hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm" aria-hidden="true" />
                  Réessayer
                </button>
              </div>
            ) : visibleNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                  <i className="ri-notification-off-line text-xl" aria-hidden="true" />
                </span>
                <p className="mt-3 text-sm font-label font-medium text-foreground-900">Aucune notification</p>
                <p className="mt-1 text-xs text-foreground-500">
                  Vos alertes sur les inscriptions, paiements, employés et contrats apparaîtront ici.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-background-200">
                {visibleNotifications.map((item) => {
                  const icon = TYPE_ICONS[item.type] ?? "ri-notification-3-line";
                  const tone = TYPE_TONES[item.type] ?? "bg-background-100 text-foreground-600";
                  const content = (
                    <>
                      <span className={cn("mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full", tone)}>
                        <i className={cn(icon, "text-base")} aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-start justify-between gap-2">
                          <span className="text-sm font-label font-medium text-foreground-950">{item.titre}</span>
                          {!item.lu && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary-500" aria-hidden="true" />}
                        </span>
                        {item.message && (
                          <span className="mt-0.5 block text-xs leading-relaxed text-foreground-600">{item.message}</span>
                        )}
                        <span className="mt-1 block text-[11px] text-foreground-400">{relativeTime(item.createdAt)}</span>
                      </span>
                    </>
                  );

                  return (
                    <li key={item.id}>
                      {item.lien ? (
                        <Link
                          to={item.lien}
                          onClick={() => handleItemClick(item)}
                          className={cn(
                            "flex items-start gap-3 px-4 py-3 transition-colors hover:bg-background-100 cursor-pointer",
                            !item.lu && "bg-primary-50/40"
                          )}
                        >
                          {content}
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleItemClick(item)}
                          className={cn(
                            "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-background-100 cursor-pointer",
                            !item.lu && "bg-primary-50/40"
                          )}
                        >
                          {content}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {visibleNotifications.length > 0 && (
            <div className="border-t border-background-200 px-4 py-2.5">
              <p className="flex items-center gap-1.5 text-[11px] text-foreground-400">
                <i className={cn(realtime ? "ri-broadcast-line" : "ri-refresh-line", "text-sm")} aria-hidden="true" />
                {realtime ? "Mise à jour en temps réel active" : "Actualisation à l'ouverture"}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}